// src/services/api-service.ts
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { configService } from './config-service'
import { Personality } from '../models/personality'

// Interfaces
export interface AuthToken {
    token: string
    expires: string
}

export interface Conversation {
    id: string
    philosopherId: string
    philosopherName: string
    created: string
    updated: string
}

export interface ApiMessage {
    id: string
    role: 'user' | 'assistant'
    content: string
    timestamp: string
}

export interface Character {
    id: string
    name: string
    type: 'stateful' | 'stateless'
}

export interface CharacterDetails extends Character {
    initialState?: string
}

export interface CharacterResponse {
    message: string
    state?: {
        currentState: string
        progress: number
    }
}

// API Service
export class ApiService {
    private static instance: ApiService

    // Authentication state
    private token: string | null = null
    private tokenExpires: string | null = null
    private authState$ = new BehaviorSubject<boolean>(false)

    // Cached data
    private personalities: Personality[] = []

    private constructor() {
        this.loadTokenFromStorage()
    }

    public static getInstance(): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService()
        }
        return ApiService.instance
    }

    // Auth Methods
    private loadTokenFromStorage(): void {
        const storedToken = localStorage.getItem('philobot_auth_token')
        const storedExpires = localStorage.getItem('philobot_token_expires')

        if (storedToken && storedExpires) {
            const now = new Date()
            const expires = new Date(storedExpires)

            if (now < expires) {
                this.token = storedToken
                this.tokenExpires = storedExpires
                this.authState$.next(true)
            } else {
                this.clearTokenFromStorage()
            }
        }
    }

    private clearTokenFromStorage(): void {
        localStorage.removeItem('philobot_auth_token')
        localStorage.removeItem('philobot_token_expires')
        this.token = null
        this.tokenExpires = null
        this.authState$.next(false)
    }

    private saveTokenToStorage(data: AuthToken): void {
        localStorage.setItem('philobot_auth_token', data.token)
        localStorage.setItem('philobot_token_expires', data.expires)
        this.token = data.token
        this.tokenExpires = data.expires
        this.authState$.next(true)
    }

    public isAuthenticated(): boolean {
        if (!this.token || !this.tokenExpires) {
            return false
        }

        const now = new Date()
        const expires = new Date(this.tokenExpires)
        return now < expires
    }

    public getAuthState(): Observable<boolean> {
        return this.authState$.asObservable()
    }

    public getAuthHeaders(): Headers {
        const headers = new Headers({
            'Content-Type': 'application/json',
        })

        if (this.token) {
            headers.append('Authorization', `Bearer ${this.token}`)
        }

        return headers
    }

    public login(): Observable<AuthToken> {
        if (this.isAuthenticated()) {
            return of({
                token: this.token as string,
                expires: this.tokenExpires as string,
            })
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/auth/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    const errorText = await response.text()
                    throw new Error(`Failed to get auth token: ${errorText}`)
                }

                const data = await response.json()
                this.saveTokenToStorage(data)
                return data
            }),
            catchError(error => {
                console.error('Failed to login:', error)
                return throwError(() => error)
            }),
        )
    }

    public logout(): Observable<void> {
        const apiBaseUrl = configService.getApiBaseUrl()

        if (!this.token) {
            this.clearTokenFromStorage()
            return of(void 0)
        }

        return from(
            fetch(`${apiBaseUrl}/auth/token`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.token}`,
                },
            }),
        ).pipe(
            tap(() => {
                this.clearTokenFromStorage()
            }),
            map(() => void 0),
            catchError(() => {
                this.clearTokenFromStorage()
                return of(void 0)
            }),
        )
    }

    // Conversation Methods
    public getConversations(): Observable<Conversation[]> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.getConversations()))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/conversations`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to get conversations: ${response.statusText}`)
                }
                const data = await response.json()
                return data.conversations || []
            }),
            catchError(error => {
                console.error('Error getting conversations:', error)
                return of([])
            }),
        )
    }

    public getConversation(conversationId: string): Observable<Conversation> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.getConversation(conversationId)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/conversations/${conversationId}`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to get conversation: ${response.statusText}`)
                }
                return await response.json()
            }),
            catchError(error => {
                console.error('Error getting conversation:', error)
                return throwError(() => error)
            }),
        )
    }

    public deleteConversation(conversationId: string): Observable<void> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.deleteConversation(conversationId)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/conversations/${conversationId}`, {
                method: 'DELETE',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            map(() => void 0),
            catchError(error => {
                console.error('Error deleting conversation:', error)
                return throwError(() => error)
            }),
        )
    }

    // Message Methods
    public getMessages(conversationId: string): Observable<ApiMessage[]> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.getMessages(conversationId)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/conversations/${conversationId}/messages`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to get messages: ${response.statusText}`)
                }
                const data = await response.json()
                return data.messages || []
            }),
            catchError(error => {
                console.error('Error getting messages:', error)
                return of([])
            }),
        )
    }

    public sendMessage(conversationId: string, content: string): Observable<ApiMessage> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.sendMessage(conversationId, content)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/conversations/${conversationId}/messages`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ content }),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to send message: ${response.statusText}`)
                }
                return await response.json()
            }),
            catchError(error => {
                console.error('Error sending message:', error)
                return throwError(() => error)
            }),
        )
    }

    // Personality Methods
    // src/services/api-service.ts

    public getPersonalities(): Observable<Personality[]> {
        // Return cached personalities if available
        if (this.personalities.length > 0) {
            return of(this.personalities)
        }

        return this.getPublicPersonalities()
    }

    // New method to get public personalities
    private getPublicPersonalities(): Observable<Personality[]> {
        const apiBaseUrl = configService.getApiBaseUrl()

        return from(
            fetch(`${apiBaseUrl}/personalities`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to get public personalities: ${response.statusText}`)
                }
                const data = await response.json()
                this.personalities = data.personalities || []
                return this.personalities
            }),
            catchError(error => {
                console.error('Error getting public personalities:', error)
                return this.getFallbackPersonalities()
            }),
        )
    }

    // Renamed from getDefaultPersonalities to getFallbackPersonalities
    private getFallbackPersonalities(): Observable<Personality[]> {
        const defaults = [
            {
                id: 'socrates',
                name: 'Socrates',
            },
            {
                id: 'aristotle',
                name: 'Aristotle',
            },
            {
                id: 'kant',
                name: 'Immanuel Kant',
            },
            {
                id: 'nietzsche',
                name: 'Friedrich Nietzsche',
            },
        ]

        this.personalities = defaults
        return of(defaults)
    }

    public createConversation(philosopherId: string): Observable<Conversation> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.createConversation(philosopherId)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/conversations`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ philosopherId }),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    const errorText = await response.text()
                    throw new Error(`Failed to create conversation: ${errorText}`)
                }
                return await response.json()
            }),
            catchError(error => {
                console.error('Error creating conversation:', error)
                return throwError(() => error)
            }),
        )
    }

    public getCharacters(): Observable<Character[]> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.getCharacters()))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/characters`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to get characters: ${response.statusText}`)
                }
                const data = await response.json()
                // Add type assertion here
                return (data.characters || []) as Character[]
            }),
            catchError(error => {
                console.error('Error getting characters:', error)
                // Add type assertion here
                return of([
                    {
                        id: 'sophia',
                        name: 'Sophia',
                        type: 'stateful',
                    },
                ] as Character[])
            }),
        )
    }

    public getCharacterDetails(characterId: string): Observable<CharacterDetails> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.getCharacterDetails(characterId)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/characters/${characterId}`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to get character details: ${response.statusText}`)
                }
                const data = await response.json()
                return (data.data || data) as CharacterDetails
            }),
            catchError(error => {
                console.error('Error getting character details:', error)
                if (characterId === 'sophia') {
                    return of({
                        id: 'sophia',
                        name: 'Sophia',
                        type: 'stateful' as const,
                        initialState: 'opener',
                    })
                }
                return throwError(() => error)
            }),
        )
    }

    public createCharacterConversation(characterId: string): Observable<Conversation> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.createCharacterConversation(characterId)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/characters/${characterId}/conversations`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(
                        `Failed to create character conversation: ${response.statusText}`,
                    )
                }
                return await response.json()
            }),
            catchError(error => {
                console.error('Error creating character conversation:', error)
                return throwError(() => error)
            }),
        )
    }

    private generateId(): string {
        return (
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15)
        )
    }

    public getCharacterHistory(characterId: string): Observable<ApiMessage[]> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.getCharacterHistory(characterId)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/characters/${characterId}/history`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to get character history: ${response.statusText}`)
                }
                const data = await response.json()
                // Convert the history format to match ApiMessage interface
                return (data.history || []).map((msg: any) => ({
                    id: msg.id || this.generateId(), // Add a helper for generating IDs
                    role: msg.role || (msg.isUser ? 'user' : 'assistant'),
                    content: msg.content || msg.message,
                    timestamp: msg.timestamp || new Date().toISOString(),
                }))
            }),
            catchError(error => {
                console.error('Error getting character history:', error)
                return of([])
            }),
        )
    }

    public sendCharacterMessage(
        characterId: string,
        message: string,
    ): Observable<CharacterResponse> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(
                switchMap(() => this.sendCharacterMessage(characterId, message)),
            )
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/characters/${characterId}/chat`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ message }),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to send message: ${response.statusText}`)
                }
                // Return the response directly since it's already in the correct format
                return response.json()
            }),
            catchError(error => {
                console.error('Error sending message to character:', error)
                return throwError(() => error)
            }),
        )
    }

    public resetCharacter(characterId: string): Observable<{ message: string }> {
        if (!this.isAuthenticated()) {
            return this.login().pipe(switchMap(() => this.resetCharacter(characterId)))
        }

        const apiBaseUrl = configService.getApiBaseUrl()
        return from(
            fetch(`${apiBaseUrl}/characters/${characterId}/reset`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
            }),
        ).pipe(
            switchMap(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to reset character: ${response.statusText}`)
                }
                const data = await response.json()
                return data.data
            }),
            catchError(error => {
                console.error('Error resetting character:', error)
                return throwError(() => error)
            }),
        )
    }
}

export const apiService = ApiService.getInstance()
